import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBars, faCheck, faFileAlt, faFilePdf, faFileImage, faPrint, faSpinner, faTimes, faUpload } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import moment from 'moment-timezone';
import '../../css/JuanScope/ScopeRegistration1.css';
import SJDEFILogo from '../../images/SJDEFILogo.png';
import SideNavigation from './SideNavigation';
import VoucherWaiverFormModal from './VoucherWaiverFormModal';
import VoucherDocumentVerificationSystem from './VoucherDocumentVerificationSystem';
import SessionManager from '../JuanScope/SessionManager';

function ScopeVoucherApplication() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState({
    email: localStorage.getItem('userEmail') || '',
    firstName: localStorage.getItem('firstName') || 'Unknown',
    middleName: localStorage.getItem('middleName') || '',
    lastName: localStorage.getItem('lastName') || 'Unknown',
    applicantID: localStorage.getItem('applicantID') || 'N/A',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);
  const [voucherType, setVoucherType] = useState('');
  const [requirements, setRequirements] = useState([
    {
      id: 1,
      name: 'QVR Certificate',
      submitted: null,
      fileUrl: null,
      fileType: null,
      waived: false,
      feedback: 'No document uploaded - Requirement must be verified or waived',
      waiverDetails: null,
    },
  ]);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    DocumentVerification,
    handleFileUpload: verificationHandleFileUpload,
    handleVerificationComplete,
    getVerificationType,
    files,
    setFiles,
    verificationResults,
    setVerificationResults,
  } = VoucherDocumentVerificationSystem();

  const styles = {
    button: {
      padding: '8px 16px',
      borderRadius: '8px',
      fontSize: '12px',
      border: 'none',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
    },
    table: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: '0',
      border: '1px solid #2A67D5',
      backgroundColor: '#CFE0FF',
      borderRadius: '8px',
      overflow: 'hidden',
    },
    th: {
      padding: '12px',
      textAlign: 'left',
      fontSize: '13px',
      fontWeight: 'bold',
      borderBottom: '1px solid #A2A2A2',
    },
    td: {
      padding: '12px',
      fontSize: '12px',
      verticalAlign: 'top',
    },
  };

  useEffect(() => {
    const fetchVoucherApplication = async () => {
      try {
        setLoading(true);
        setError('');
        const userEmail = userData.email.toLowerCase().trim();
        if (!userEmail) throw new Error('User email not found.');

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/enrollee-applicants/voucher-application/${userEmail}`,
          { timeout: 10000 }
        );

        const { voucherType, voucherRequirements, voucherApplicationStatus } = response.data;
        setVoucherType(voucherType || '');
        setIsSubmitted(voucherApplicationStatus === 'Complete');

        if (voucherType === 'PEAC VOUCHER' && Array.isArray(voucherRequirements)) {
          const updatedRequirements = voucherRequirements.map((r) => ({
            id: r.requirementId,
            name: r.name || 'QVR Certificate',
            submitted: r.fileName || null,
            fileUrl: r.voucherRequirementsFileUrl || (r.fileName
              ? `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/voucher-documents/${userEmail}/${r.requirementId}/${r.fileName}`
              : null),
            fileType: r.fileType || null,
            waived: r.status === 'Waived',
            feedback:
              r.status === 'Waived'
                ? 'Waiver approved'
                : r.status === 'Verified'
                ? 'Document verified'
                : r.status === 'Submitted'
                ? 'Document uploaded'
                : r.status === 'Invalid'
                ? 'Verification failed'
                : 'No document uploaded - Requirement must be verified or waived',
            waiverDetails: r.waiverDetails || null,
          }));
          setRequirements(updatedRequirements);
        }
      } catch (err) {
        console.error('Fetch voucher application error:', err);
        setError(err.response?.data?.error || 'Failed to load voucher application data.');
      } finally {
        setLoading(false);
      }
    };

    if (userData.email && userData.email !== '') {
      fetchVoucherApplication();
    } else {
      setLoading(false);
      navigate('/scope-login', { state: { error: 'No active session found. Please log in.' } });
    }
  }, [userData.email, navigate]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isFormDirty && !isSubmitted) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isFormDirty, isSubmitted]);

  useEffect(() => {
    Object.entries(verificationResults).forEach(([id, result]) => {
      if (result.status !== 'pending') {
        setRequirements((prev) =>
          prev.map((req) =>
            req.id === parseInt(id)
              ? {
                  ...req,
                  feedback: result.status === 'verified' ? 'Document verified' : result.message || 'Verification failed',
                }
              : req
          )
        );
      }
    });
  }, [verificationResults]);

  const getOverallStatus = () => {
    if (!requirements.length) return { status: 'Unverified', isValid: false };
    const isValid = requirements.every((req) => req.waived || req.feedback === 'Document verified');
    return { status: isValid ? 'Valid' : 'Invalid', isValid };
  };

  const handleVoucherTypeChange = (e) => {
    if (isSubmitted) {
      alert('Voucher application is complete. You cannot change the voucher type.');
      return;
    }
    const newType = e.target.value;
    setVoucherType(newType);
    setIsFormDirty(true);
    if (newType !== 'PEAC VOUCHER') {
      setRequirements([
        {
          id: 1,
          name: 'QVR Certificate',
          submitted: null,
          fileUrl: null,
          fileType: null,
          waived: false,
          feedback: 'No document uploaded - Requirement must be verified or waived',
          waiverDetails: null,
        },
      ]);
      setUploadedFiles({});
      setFiles({});
      setVerificationResults({});
    }
  };

  const handleFileUpload = (id, event) => {
    if (isSubmitted) {
      alert('Voucher application is complete. You cannot upload files.');
      return;
    }
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert('Only PNG, JPG, or PDF files are allowed.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must not exceed 10MB.');
      return;
    }

    setUploadedFiles((prev) => ({ ...prev, [id]: file }));
    verificationHandleFileUpload(id, event);
    setRequirements((prev) =>
      prev.map((req) =>
        req.id === id
          ? {
              ...req,
              submitted: file.name,
              fileType: file.type,
              feedback: 'Document uploaded',
              waiverDetails: null,
              waived: false,
            }
          : req
      )
    );
    setIsFormDirty(true);
  };

  const handleViewDocument = async (id) => {
    try {
      const requirement = requirements.find((req) => req.id === id);
      if (!requirement?.submitted || requirement.waived) {
        alert('No file available to view.');
        return;
      }

      if (requirement.fileUrl) {
        window.open(requirement.fileUrl, '_blank');
      } else {
        alert('File URL not available. Please save your changes first.');
      }
    } catch (err) {
      console.error('View document error:', err);
      alert('Failed to view document. Please try again.');
    }
  };

  const handleWaiveCredentials = () => {
    if (isSubmitted) {
      alert('Voucher application is complete. You cannot waive credentials.');
      return;
    }
    setShowWaiverModal(true);
  };

  const handleWaiverSubmit = (waiverData) => {
    setRequirements((prev) =>
      prev.map((req) => {
        if (waiverData.selectedRequirements.includes(req.id)) {
          return {
            ...req,
            waived: true,
            submitted: null,
            fileUrl: null,
            fileType: null,
            feedback: 'Waiver requested',
            waiverDetails: {
              reason: waiverData.reason || 'Not specified',
              promiseDate: waiverData.promiseDate || moment().add(30, 'days').format('YYYY-MM-DD'),
            },
          };
        }
        if (req.waived && !waiverData.selectedRequirements.includes(req.id)) {
          return {
            ...req,
            waived: false,
            feedback: 'No document uploaded - Requirement must be verified or waived',
            waiverDetails: null,
          };
        }
        return req;
      })
    );
    setIsFormDirty(true);
    setShowWaiverModal(false);
  };

  const handlePrintWaiver = async () => {
    const waivedRequirements = requirements.filter((req) => req.waived && req.waiverDetails);
    if (!waivedRequirements.length) {
      alert('No waived requirements to print.');
      return;
    }

    try {
      const payload = {
        userData: {
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
        },
        waivedRequirements,
        academicYear: '2025-2026',
        dateIssued: moment().tz('Asia/Manila').format('YYYY-MM-DD'),
        dateSigned: moment().tz('Asia/Manila').format('YYYY-MM-DDTHH:mm:ssZ'),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/pdf/generate-voucher-waiver-pdf`,
        payload,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Print waiver error:', err);
      setError('Failed to generate waiver PDF. Please try again.');
    }
  };

  const handleSave = async () => {
    try {
      const userEmail = userData.email;
      const formData = new FormData();
      formData.append('email', userEmail);
      formData.append('voucherType', voucherType);

      if (voucherType === 'PEAC VOUCHER') {
        const formattedRequirements = requirements.map((req) => ({
          id: req.id,
          name: req.name,
          waived: req.waived,
          status: req.waived
            ? 'Waived'
            : verificationResults[req.id]?.status === 'verified'
            ? 'Verified'
            : req.submitted
            ? 'Submitted'
            : 'Not Submitted',
          waiverDetails: req.waived
            ? {
                reason: req.waiverDetails?.reason || 'Not specified',
                promiseDate: req.waiverDetails?.promiseDate || moment().add(30, 'days').toISOString(),
              }
            : undefined,
          file: req.submitted && !req.waived ? { fileName: req.submitted, fileType: req.fileType } : undefined,
        }));
        formData.append('requirements', JSON.stringify(formattedRequirements));
        Object.entries(uploadedFiles).forEach(([id, file]) => {
          formData.append(`file-${id}`, file);
        });
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/enrollee-applicants/save-voucher-application`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const { voucherType: savedVoucherType, voucherRequirements, voucherApplicationStatus } = response.data;

      if (savedVoucherType === 'PEAC VOUCHER' && Array.isArray(voucherRequirements)) {
        const updatedRequirements = voucherRequirements.map((r) => ({
          id: r.requirementId,
          name: r.name || 'QVR Certificate',
          submitted: r.fileName || null,
          fileUrl: r.voucherRequirementsFileUrl || (r.fileName
            ? `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/voucher-documents/${userEmail}/${r.requirementId}/${r.fileName}`
            : null),
          fileType: r.fileType || null,
          waived: r.status === 'Waived',
          feedback:
            r.status === 'Waived'
              ? 'Waiver approved'
              : r.status === 'Verified'
              ? 'Document verified'
              : r.status === 'Submitted'
              ? 'Document uploaded'
              : r.status === 'Invalid'
              ? 'Verification failed'
              : 'No document uploaded - Requirement must be verified or waived',
          waiverDetails: r.waiverDetails || null,
        }));
        setRequirements(updatedRequirements);
      }

      setVoucherType(savedVoucherType);
      setIsSubmitted(voucherApplicationStatus === 'Complete');
      setIsFormDirty(false);
      setUploadedFiles({});
      alert('Voucher application saved successfully!');
      return true;
    } catch (err) {
      console.error('Save voucher application error:', err);
      setError('Failed to save voucher application. Please try again.');
      return false;
    }
  };

  const handleConfirmSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError('');

    try {
      const saveSuccess = await handleSave();
      if (!saveSuccess) {
        throw new Error('Failed to save voucher application.');
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/enrollee-applicants/complete-voucher-application`,
        { email: userData.email, voucherType }
      );

      if (response.status === 200) {
        setIsSubmitted(true);
        setIsFormDirty(false);
        alert('Voucher application submitted successfully!');
        navigate('/scope-enrollment-approval');
      } else {
        throw new Error('Unexpected response from server.');
      }
    } catch (err) {
      console.error('Submission error:', err);
      setError(err.response?.data?.error || 'Submission failed. Please try again.');
      alert(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
      setShowConfirmModal(false);
    }
  };

  const handleNext = () => {
    if (!voucherType) {
      alert('Please select a voucher type to apply.');
      return;
    }
    if (voucherType === 'PEAC VOUCHER' && !getOverallStatus().isValid && !isSubmitted) {
      alert('The QVR Certificate must be verified or waived to proceed.');
      return;
    }
    if (isSubmitted) {
      navigate('/scope-enrollment-approval');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleBack = () => {
    if (isFormDirty && !isSubmitted) {
      setNextLocation('/scope-enrollment-requirements');
      setShowUnsavedModal(true);
    } else {
      navigate('/scope-enrollment-requirements');
    }
  };

  return (
    <SessionManager>
      <div className="scope-registration-container">
        <header className="juan-register-header">
          <div className="juan-header-left">
            <img src={SJDEFILogo} alt="SJDEFI Logo" className="juan-logo-register" />
            <h1>JUAN SCOPE</h1>
          </div>
          <button className="hamburger-button" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle Sidebar">
            <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} size="lg" />
          </button>
        </header>

        <div className="scope-registration-content">
          <SideNavigation
            userData={userData}
            onNavigate={() => setSidebarOpen(false)}
            isOpen={sidebarOpen}
          />
          <main className={`scope-main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
            {loading ? (
              <div className="scope-loading">Loading...</div>
            ) : error ? (
              <div className="scope-error">{error}</div>
            ) : (
              <div className="registration-content">
                <h2 className="registration-title">Voucher Application</h2>
                <div className="registration-divider" />
                <div className="registration-container">
                  <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '1rem' }}>
                    Select a voucher type and submit required documents to apply for financial assistance.
                  </div>
                  {isSubmitted && (
                    <div style={{ margin: '1rem 0', color: '#333', fontSize: '14px', backgroundColor: '#e0f7fa', padding: '1rem', borderRadius: '5px' }}>
                      Your voucher application has been successfully submitted and is complete.
                    </div>
                  )}
                  <div className="personal-info-section">
                    <div className="personal-info-header">
                      <FontAwesomeIcon icon={faFileAlt} style={{ color: '#212121' }} />
                      <h3>{isSubmitted ? 'View Voucher Application' : 'Apply for Voucher'}</h3>
                    </div>
                    <div className="personal-info-divider" />
                    {!isSubmitted && (
                      <div className="reminder-box">
                        <p>
                          <strong>Reminders:</strong>
                          <ul style={{ paddingLeft: '20px', margin: '5px 0 0 0', listStyleType: 'disc', fontSize: '12px' }}>
                            <li>Only one voucher application is allowed at a time.</li>
                            <li>After the Academic Voucher period ends, only Financial and Special Discount applications are accepted.</li>
                            <li>Accepted formats: PNG, JPG, PDF (max 10MB).</li>
                          </ul>
                        </p>
                      </div>
                    )}
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                      <label style={styles.label}>
                        Select Voucher Type: <span className="required-asterisk">*</span>
                      </label>
                      <select
                        value={voucherType}
                        onChange={handleVoucherTypeChange}
                        disabled={isSubmitted}
                        style={{
                          width: '100%',
                          padding: '8px',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                          fontSize: '14px',
                        }}
                      >
                        <option value="">Select a voucher type</option>
                        <option value="PUBLIC SCHOOL VOUCHER">PUBLIC SCHOOL VOUCHER</option>
                        <option value="PRIVATE SCHOOL WITHOUT VOUCHER">PRIVATE SCHOOL WITHOUT VOUCHER</option>
                        <option value="PEAC VOUCHER">PEAC VOUCHER</option>
                      </select>
                    </div>
                    {voucherType === 'PEAC VOUCHER' && (
                      <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
                        <table style={styles.table}>
                          <thead>
                            <tr style={{ backgroundColor: '#2A67D5', color: 'white' }}>
                              {!isSubmitted && <th style={styles.th}>Actions</th>}
                              <th style={styles.th}>Requirement</th>
                              <th style={styles.th}>Submitted Copy</th>
                              <th style={styles.th}>Waived</th>
                              <th style={styles.th}>Feedback</th>
                            </tr>
                          </thead>
                          <tbody>
                            {requirements.map((req) => (
                              <tr
                                key={req.id}
                                style={{
                                  backgroundColor: 'white',
                                  transition: 'background-color 0.2s',
                                  border: req.feedback.includes('Verification failed') ? '2px solid red' : 'none',
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                              >
                                {!isSubmitted && (
                                  <td style={styles.td}>
                                    <label
                                      style={{
                                        ...styles.button,
                                        backgroundColor: '#00245A',
                                        color: 'white',
                                        cursor: req.waived ? 'not-allowed' : 'pointer',
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faUpload} />
                                      Upload
                                      <input
                                        type="file"
                                        accept=".png,.jpg,.jpeg,.pdf"
                                        style={{ display: 'none' }}
                                        onChange={(e) => handleFileUpload(req.id, e)}
                                        disabled={req.waived || isSubmitted}
                                      />
                                    </label>
                                  </td>
                                )}
                                <td style={styles.td}>
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    {req.name}
                                    {req.feedback === 'Document verified' && (
                                      <FontAwesomeIcon icon={faCheck} style={{ color: '#34A853' }} />
                                    )}
                                    {req.feedback.includes('Verification failed') && (
                                      <FontAwesomeIcon icon={faTimes} style={{ color: '#EA4335' }} />
                                    )}
                                  </div>
                                </td>
                                <td style={styles.td}>
                                  {req.submitted && !req.waived ? (
                                    req.fileUrl ? (
                                      <a
                                        href={req.fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          textDecoration: 'none',
                                          color: '#2A67D5',
                                          fontSize: '12px',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '5px',
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={req.fileType?.includes('pdf') ? faFilePdf : faFileImage}
                                        />
                                        {req.submitted}
                                      </a>
                                    ) : (
                                      <button
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          color: '#2A67D5',
                                          fontSize: '12px',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '5px',
                                        }}
                                        onClick={() => handleViewDocument(req.id)}
                                      >
                                        <FontAwesomeIcon
                                          icon={req.fileType?.includes('pdf') ? faFilePdf : faFileImage}
                                        />
                                        {req.submitted}
                                      </button>
                                    )
                                  ) : (
                                    '-'
                                  )}
                                </td>
                                <td style={styles.td}>
                                  {req.waived ? <FontAwesomeIcon icon={faCheck} style={{ color: '#34A853' }} /> : '-'}
                                </td>
                                <td style={{ ...styles.td, whiteSpace: 'pre-line' }}>{req.feedback}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {!isSubmitted && (
                          <div style={{ display: 'flex', gap: '10px', marginTop: '1rem', flexWrap: 'wrap' }}>
                            <button
                              style={{ ...styles.button, backgroundColor: '#00245A', color: 'white' }}
                              onClick={handleWaiveCredentials}
                              disabled={isSubmitted}
                            >
                              <FontAwesomeIcon icon={faFileAlt} />
                              Waive Credentials
                            </button>
                            <button
                              style={{ ...styles.button, backgroundColor: '#4285F4', color: 'white' }}
                              onClick={handlePrintWaiver}
                            >
                              <FontAwesomeIcon icon={faPrint} />
                              Print Waiver
                            </button>
                          </div>
                        )}
                        <div style={{ display: 'none' }}>
                          {requirements.map((req) => (
                            <DocumentVerification
                              key={req.id}
                              file={files[req.id]}
                              requirementType={getVerificationType(req.name)}
                              onVerificationComplete={(result) => handleVerificationComplete(req.id, result)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="form-buttons">
                      <button type="button" className="back-button" onClick={handleBack}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Back
                      </button>
                      <button
                        type="button"
                        className="next-button"
                        onClick={handleNext}
                        disabled={!voucherType || (voucherType === 'PEAC VOUCHER' && !getOverallStatus().isValid && !isSubmitted)}
                        style={{
                          backgroundColor:
                            voucherType && (voucherType !== 'PEAC VOUCHER' || getOverallStatus().isValid || isSubmitted)
                              ? '#34A853'
                              : '#d3d3d3',
                          cursor:
                            voucherType && (voucherType !== 'PEAC VOUCHER' || getOverallStatus().isValid || isSubmitted)
                              ? 'pointer'
                              : 'not-allowed',
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>

        {sidebarOpen && <div className="sidebar-overlay active" onClick={() => setSidebarOpen(false)} />}
        <VoucherWaiverFormModal
          isOpen={showWaiverModal}
          onClose={() => setShowWaiverModal(false)}
          onSubmit={handleWaiverSubmit}
          requirements={requirements}
          userData={userData}
        />
        {showConfirmModal && (
          <div className="scope-modal-overlay">
            <div className="scope-confirm-modal">
              <h3>Confirm Submission</h3>
              <p>You are about to submit your voucher application. Once submitted, you cannot make changes. Are you sure?</p>
              <div className="scope-modal-buttons">
                <button className="scope-modal-cancel" onClick={() => setShowConfirmModal(false)} disabled={isSubmitting}>
                  Cancel
                </button>
                <button
                  className={`scope-modal-confirm ${isSubmitting ? 'scope-modal-button-loading' : ''}`}
                  onClick={handleConfirmSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="scope-modal-spinner" />
                      Processing...
                    </>
                  ) : (
                    'Submit'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        {showUnsavedModal && (
          <div className="scope-modal-overlay">
            <div className="scope-confirm-modal">
              <h3>Unsaved Changes</h3>
              <p>You have unsaved changes. Do you want to leave without saving?</p>
              <div className="scope-modal-buttons">
                <button className="scope-modal-cancel" onClick={() => setShowUnsavedModal(false)}>
                  Cancel
                </button>
                <button
                  className="scope-modal-confirm"
                  onClick={() => {
                    setShowUnsavedModal(false);
                    navigate(nextLocation);
                  }}
                >
                  Leave
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SessionManager>
  );
}

export default ScopeVoucherApplication;